/**
 * menuParent — resolves the "menu parent" (the next page UP in the menu chain)
 * for a given pathname, so a Back button lands on the logical parent page of
 * the menu instead of the browser-history "previously accessed page".
 *
 * The menu chain (from the `menu` table / v2/UserProfile/MenuList) is:
 *
 *   Dashboard (MENU_ROOT)
 *     └─ Section group   (Masters, Accounting, Operations, Advisory, MIS Report…)
 *          └─ Menu leaf  (the list / manage page, e.g. /clients)
 *               └─ Sub-page (add / edit / view / archived / tab / passbook…)
 *
 * Resolution rules
 *   • Sub-page  → its menu leaf (list page).
 *   • Menu leaf → its section landing page, when the section HAS a page
 *     (/advisory-header, /operation-header, /compliance-header, /portfolio,
 *     /mis-report, /reports, /actions, /bulk-import …).
 *   • Menu leaf whose section has NO page (Masters, Accounting, HR, SA-Masters,
 *     Product masters, SA-Operations, Planning…) → not mapped here; the hook
 *     falls back to MENU_ROOT (/dashboard), the top of every chain.
 *   • Browser history is never used (see hooks/useMenuBack.ts).
 *
 * Matching rule: a child pattern matches when the pathname equals it exactly
 * or starts with `child + "/"` (so trailing `:id` params are absorbed). A child
 * pattern may also carry `:name` segments; those are captured and substituted
 * into the parent (e.g. passbook-entry add → passbook page of the same account).
 * When several children match, the LONGEST pattern wins — this lets a leaf
 * (child of the section landing) coexist with its own sub-pages.
 *
 * NOTE: route strings intentionally mirror the router's real (sometimes
 * misspelled / inconsistently cased) paths — e.g. `ppf-acount`,
 * `amotization-loan`, `epf-archieved`, `archeived-bullion-sip`,
 * `realestate-archived-acount`, `Aif-providers-*`, `accouting/*`. Do not
 * "correct" them.
 */

/** Top of every menu chain — used by useMenuBack when nothing else matches. */
export const MENU_ROOT = "/dashboard";

type Group = { parent: string; children: string[] };

const GROUPS: Group[] = [
  /* ═══════════════════════ DASHBOARD cards ═══════════════════════ */
  {
    parent: "/dashboard",
    children: [
      "/dashboard/new-risk-profile",
      "/dashboard/submit-data-view-details",
      "/dashboard/edit-aua-asset",
      "/dashboard/client-portfolio-view-detail",
      "/dashboard/sip-book-view-detail",
      "/dashboard/expiring-systematic-plans",
      "/dashboard/asset-under-management",
      "/dashboard/locked-accounts",
      "/dashboard/business-accreditations",
      "/dashboard/noagreement",
      "/dashboard/PendingAgreements",
      "/dashboard/Signed-Agreements",
      "/asset-pending-for-mapping",
      "/goals-pending-for-mapping",
      "/admin/client-birthday",
      "/admin/client-anniversary",
      "/admin/employee-birthday",
      "/client-notes",
      "/my-profile",
      "/change-password",
      "/admin/dashboard",
      "/alerts",
      "/discrepancies",
      "/documents",
      "/mails",
      "/help-window",
    ],
  },
  {
    /* New Risk Profile wizard — the landing page is the section root
       (the old /dashboard/risk-profile page was removed 22-Sep-2026) */
    parent: "/dashboard/new-risk-profile",
    children: [
      "/dashboard/start-new-risk-profile",
      "/dashboard/start-new-risk-profile-manual",
      "/dashboard/start-new-risk-profile-system",
      "/dashboard/new-risk-profile-disclaimers",
      "/dashboard/risk-profile-history",
    ],
  },
  {
    /* Override is reached from the Disclaimers page, so Back returns there */
    parent: "/dashboard/new-risk-profile-disclaimers",
    children: ["/dashboard/new-risk-profile-override"],
  },
  {
    /* Override Disclaimer follows Override, so Back returns there */
    parent: "/dashboard/new-risk-profile-override",
    children: ["/dashboard/override-disclaimer"],
  },
  {
    parent: "/dashboard/submit-data-view-details",
    children: [
      "/dashboard/discarded-submit-data-view-details",
      "/dashboard/edit-submit-data-view-details",
    ],
  },
  {
    parent: "/dashboard/asset-under-management",
    children: [
      "/dashboard/asset-under-management-shares",
      "/dashboard/aum-manage",
    ],
  },

  /* ═══════════════════════ INFORMATION (client) ═══════════════════════ */
  {
    parent: "/information/loans",
    children: [
      "/information/add-loan",
      "/information/edit-loan",
      "/information/archived-loan",
      "/information/amotization-loan",
      "/information/import-amotization-loan",
      "/information/generate-amotization-loan",
    ],
  },
  {
    parent: "/information/accounts",
    children: [
      "/information/add-bank-account",
      "/information/edit-bank-account",
      "/information/add-dmat-account",
      "/information/edit-dmat-account",
      "/information/add-investment-account",
      "/information/edit-investment-account",
      "/information/mandates",
      "/information/add-mandates",
      "/information/edit-mandates",
      "/information/archived-mandates",
    ],
  },
  {
    parent: "/information/income",
    children: [
      "/information/add-income",
      "/information/edit-income",
      "/information/view-edit-income",
      "/information/archived-income",
    ],
  },
  {
    parent: "/information/personal/family",
    children: [
      "/information/personal/add-member",
      "/information/personal/edit-member",
      "/information/personal/edit-primary-member",
      "/information/personal/archived-family-member",
    ],
  },
  {
    parent: "/information/personal/address",
    children: [
      "/information/personal/add-address",
      "/information/personal/edit-address",
      "/information/personal/archived-address",
    ],
  },
  {
    parent: "/information/personal/health-lifestyle",
    children: [
      "/information/personal/add-health-lifestyle",
      "/information/personal/edit-health-lifestyle",
    ],
  },
  {
    parent: "/information/personal/occupation",
    children: [
      "/information/personal/add-occupation",
      "/information/personal/edit-occupation",
      "/information/personal/view-edit-occupation",
      "/information/personal/archived-occupation",
    ],
  },
  {
    parent: "/information/physical-assets/vehicles",
    children: [
      "/information/physical-assets/add-vehicles",
      "/information/physical-assets/edit-vehicles",
      "/information/physical-assets/archived-vehicles",
    ],
  },
  {
    // NB: household add/edit live under the /personal/ prefix, but the list
    // (menu item) is under /physical-assets/.
    parent: "/information/physical-assets/household",
    children: [
      "/information/personal/add-household",
      "/information/personal/edit-household",
      "/information/personal/multiple-add",
      "/information/personal/archived-household",
    ],
  },

  /* ═══════════════════════ GOALS / INSURANCE / EXPENSES / PLANNING (client) ═══════════════════════ */
  {
    parent: "/goals",
    children: [
      "/goals/add-goal",
      "/goals/edit-goal",
      "/goals/view-goal",
      "/goals/archeived-goal",
      "/add-goal",
      "/edit-goal",
      "/view-goal",
      "/archeived-goal",
    ],
  },
  {
    parent: "/insurance",
    children: [
      "/insurance/add-insurance-details",
      "/insurance/edit-insurance-details",
      "/insurance/archived-riders",
      "/insurance-riders",
      "/insurance/payout-details",
      "/archived-insurance",
      "/future-insurance",
      "/add-money-back-payout",
      "/update-money-back-payout",
    ],
  },
  {
    // rider add/edit → the riders list of the same policy
    parent: "/insurance-riders/:id",
    children: ["/insurance/add-riders/:id", "/insurance/edit-riders/:id"],
  },
  {
    parent: "/expenses",
    children: ["/expenses-detail", "/expenses-detail-saved", "/expenses-minimum"],
  },
  {
    parent: "/planing/life-insrance-requirement",
    children: ["/financial-planing/life-insurance-expenses"],
  },

  /* ═══════════════════════ PORTFOLIO: module lists → Portfolio landing ═══════════════════════ */
  {
    parent: "/portfolio",
    children: [
      "/portfolio/fixed-return",
      "/portfolio/ppf-acount",
      "/portfolio/ssy-acount",
      "/portfolio/bonds-debentures",
      "/portfolio/realestate-properties",
      "/portfolio/bullion-investments",
      "/portfolio/epf-account",
      "/portfolio/mutual-funds",
      "/portfolio/nps-details",
      "/portfolio/superannuation-account",
      "/portfolio/shares-etf",
      "/portfolio/cash-equivalents",
      "/portfolio/insurance",
      "/portfolio/pms-portfolio-manage",
      "/portfolio/aif-portfolio-manage",
      "/portfolio/others",
      "/portfolio/pms",
      "/portfolio/aif",
    ],
  },

  /* ═══════════════════════ PORTFOLIO: sub-pages → their module list ═══════════════════════ */
  {
    parent: "/portfolio/fixed-return",
    children: [
      "/portfolio/add-fixed-return",
      "/portfolio/edit-fixed-return",
      "/portfolio/view-fixed-return",
      "/portfolio/fixed-return-archived",
      "/portfolio/fixed-return-interst-records",
      "/portfolio/fixed-return-passbook",
      "/portfolio/archived-fixed-return-interst-records",
    ],
  },
  {
    parent: "/portfolio/ppf-acount",
    children: [
      "/portfolio/add-ppf-acount",
      "/portfolio/edit-ppf-acount",
      "/portfolio/ppf-passbook",
      "/portfolio/add-ppfpassbook-entry",
      "/portfolio/edit-ppfpassbook-entry",
      "/portfolio/ppf-archived-acount",
    ],
  },
  {
    parent: "/portfolio/ppf-passbook/:id1",
    children: [
      "/portfolio/add-ppfpassbook-entry/:id1",
      "/portfolio/edit-ppfpassbook-entry/:id1/:id2",
    ],
  },
  {
    parent: "/portfolio/ssy-acount",
    children: [
      "/portfolio/ssy-add-acount",
      "/portfolio/ssy-edit-acount",
      "/portfolio/ssy-archived-acount",
      "/portfolio/ssy-account-passbook",
      "/portfolio/add-ssy-passbook",
      "/portfolio/edit-ssy-passbook",
    ],
  },
  {
    parent: "/portfolio/ssy-account-passbook/:id1",
    children: [
      "/portfolio/add-ssy-passbook/:id1",
      "/portfolio/edit-ssy-passbook/:id1/:id2",
    ],
  },
  {
    parent: "/portfolio/bonds-debentures",
    children: [
      "/portfolio/add-bonds-debentures",
      "/portfolio/edit-bonds-debentures",
      "/portfolio/archived-bonds-debentures",
    ],
  },
  {
    parent: "/portfolio/realestate-properties",
    children: [
      "/portfolio/add-realestate-properties",
      "/portfolio/edit-realestate-properties",
      "/portfolio/realestate-archived-acount",
    ],
  },
  {
    parent: "/portfolio/bullion-investments",
    children: [
      "/portfolio/add-bullion-investments",
      "/portfolio/edit-bullion-investments",
      "/portfolio/archived-bullion-investments",
      "/portfolio/add-bullion-sip",
      "/portfolio/edit-bullion-sip",
      "/portfolio/archeived-bullion-sip",
    ],
  },
  {
    parent: "/portfolio/epf-account",
    children: [
      "/portfolio/add-epf-details",
      "/portfolio/edit-epf-details",
      "/portfolio/epf-passbook",
      "/portfolio/add-epf-passbook-entry",
      "/portfolio/edit-epf-passbook-entry",
      "/portfolio/epf-archieved",
    ],
  },
  {
    parent: "/portfolio/epf-passbook/:id1",
    children: [
      "/portfolio/add-epf-passbook-entry/:id1",
      "/portfolio/edit-epf-passbook-entry/:id1/:id2",
    ],
  },
  {
    parent: "/portfolio/mutual-funds",
    children: [
      "/portfolio/mutual-funds-transaction",
      "/portfolio/update-mutualfunds-transaction",
      "/portfolio/archived-mutual-funds",
      "/portfolio/mutual-funds-transaction-archived",
      "/portfolio/mutual-fund/systematic-archived",
      "/portfolio/mutual-fund/systematic-future",
      "/portfolio/add-sip-stp-swp",
      "/portfolio/edit-sip-stp-swp",
    ],
  },
  {
    parent: "/portfolio/nps-details",
    children: [
      "/portfolio/add-nps-details",
      "/portfolio/edit-nps-details",
      "/portfolio/nps-details-archived",
    ],
  },
  {
    parent: "/portfolio/superannuation-account",
    children: [
      "/portfolio/add-superannuation-account",
      "/portfolio/edit-superannuation-account",
      "/portfolio/archived-superannuation-account",
    ],
  },
  {
    parent: "/portfolio/shares-etf",
    children: [
      "/portfolio/change-dmat",
      "/portfolio/holding-archived-shares-etf",
      "/portfolio/shares-etf-transaction",
      "/portfolio/shares-etf-transaction-archived",
      "/portfolio/add-share-epf-sip",
      "/portfolio/edit-share-epf-sip",
      "/portfolio/archeived-share-sip",
      "/portfolio/update-shareetf-transaction",
    ],
  },
  {
    parent: "/portfolio/pms-portfolio-manage",
    children: [
      "/portfolio/pms-portfolio-add",
      "/portfolio/pms-portfolio-edit",
      "/portfolio/pms-portfolio-archived",
      "/portfolio/pmspassbook-entry-manage",
      "/portfolio/pmspassbook-entry-add",
      "/portfolio/pmspassbook-entry-edit",
    ],
  },
  {
    parent: "/portfolio/pmspassbook-entry-manage/:id1",
    children: [
      "/portfolio/pmspassbook-entry-add/:id1",
      "/portfolio/pmspassbook-entry-edit/:id1/:id2",
    ],
  },
  {
    parent: "/portfolio/aif-portfolio-manage",
    children: [
      "/portfolio/aif-portfolio-add",
      "/portfolio/aif-portfolio-edit",
      "/portfolio/aif-portfolio-archived",
      "/portfolio/aifpassbook-entry-manage",
      "/portfolio/aifpassbook-entry-add",
      "/portfolio/aifpassbook-entry-edit",
    ],
  },
  {
    parent: "/portfolio/aifpassbook-entry-manage/:id1",
    children: [
      "/portfolio/aifpassbook-entry-add/:id1",
      "/portfolio/aifpassbook-entry-edit/:id1/:id2",
    ],
  },
  {
    parent: "/portfolio/others",
    children: [
      "/portfolio/others-portfolio-add",
      "/portfolio/others-portfolio-edit",
      "/portfolio/others-portfolio-archived",
      "/portfolio/otherspassbook-entry-manage",
      "/portfolio/otherspassbook-entry-add",
      "/portfolio/otherspassbook-entry-edit",
    ],
  },
  {
    parent: "/portfolio/otherspassbook-entry-manage/:id1",
    children: [
      "/portfolio/otherspassbook-entry-add/:id1",
      "/portfolio/otherspassbook-entry-edit/:id1/:id2",
    ],
  },

  /* ═══════════════════════ ACTIONS (Tasks hub) ═══════════════════════ */
  {
    parent: "/actions",
    children: [
      "/actions/my-task",
      "/actions/client-task",
      "/actions/assign-task",
      "/actions/manager",
      "/actions/recurring",
      "/actions/fintech-task",
      "/actions/fintech-assign-task",
      "/actions/fintech-my-task",
      "/actions/internal-fintech-my-task",
      "/actions/document-tracker",
      "/action/minutes-of-meeting",
      "/admin/action-client's-tasks",
      "/fintech-actions-task",
    ],
  },
  {
    parent: "/actions/my-task",
    children: [
      "/actions/my-task/edit",
      "/actions/my-task-hibernated",
      "/actions/my-action-archived",
      "/actions/add-action",
      "/actions/edit-action",
      "/actions/edit-task",
      "/action/view-edit-action",
      "/action/view-edit-task",
    ],
  },
  {
    parent: "/actions/assign-task",
    children: ["/actions/archived-assigned-task"],
  },
  {
    parent: "/actions/manager",
    children: ["/actions/unassign-manager", "/actions/reassign-manager"],
  },
  {
    parent: "/actions/recurring",
    children: [
      "/actions/add-recurring",
      "/actions/edit-recurring",
      "/actions/archived-recurring",
    ],
  },
  {
    parent: "/actions/fintech-task",
    children: [
      "/actions/fintech-task-archived",
      "/actions/add-fintech-task",
      "/actions/edit-fintech-client-task",
      "/action/view-fintech-task",
      "/action/view-edit-fintech-task",
    ],
  },
  {
    parent: "/actions/fintech-assign-task",
    children: [
      "/actions/fintech-assign-task-archived",
      "/actions/add-fintech-assign-task",
      "/actions/edit-fintech-task",
      "/action/view-assign-fintech-task",
    ],
  },
  {
    parent: "/actions/internal-fintech-my-task",
    children: ["/actions/internal-fintech-task-archived"],
  },
  {
    parent: "/actions/document-tracker",
    children: [
      "/actions/add-document-tracker",
      "/actions/edit-document-tracker",
      "/actions/archived-document-tracker",
    ],
  },
  {
    parent: "/action/minutes-of-meeting",
    children: [
      "/action/add-minutes-of-meeting",
      "/action/edit-minutes-of-meeting",
      "/action/received-for-review",
      "/actions/mom-sentfor-review",
      "/submitted-clients",
      "/archived-mom",
      "/information/view-minutes-of-meeting",
    ],
  },
  {
    parent: "/raise/advice/request",
    children: [
      "/raise/advice/add-client-request",
      "/raise/advice/archeived-client-request",
      "/raise/advice/edit-Lum-sum_investment-client-request",
      "/raise/advice/edit-Sip_investment-client-request",
      "/raise/advice/edit-redemption-client-request",
      "/raise/advice/edit-Sip_Cancellation-client-request",
      "/raise/advice/edit-Sip_Pause-client-request",
    ],
  },
  {
    parent: "/advice-request",
    children: ["/add-advice-request"],
  },
  {
    // "My Documents" (top-bar menu)
    parent: "/action/my-document",
    children: [
      "/action/add-document",
      "/action/edit-document",
      "/action/view-edit-document",
      "/action/archived-document",
    ],
  },

  /* ═══════════════════════ MIS REPORT (client) → /reports ═══════════════════════ */
  {
    parent: "/reports",
    children: [
      "/reports/transaction-report",
      "/reports/holding-report",
      "/reports/capital-gain-report",
      "/reports/client-capital-gain-report",
      "/reports/notional-capital-gain-report",
      "/reports/tax",
      "/reports/invoices",
      "/reports/cash-flow",
      "/reports/portfolio-valuation-report",
      "/reports/client-transaction-report",
      "/reports/client-investor-report",
      "/report",
    ],
  },
  {
    parent: "/reports/tax",
    children: [
      "/reports/tax-calculation-report",
      "/reports/tax-calculation-old-regime-report",
      "/reports/client/tax-calculation-report",
      "/reports/client/tax-calculation-old-regime-report",
    ],
  },
  {
    parent: "/reports/invoices",
    children: ["/reports/paid-invoices"],
  },

  /* ═══════════════════════ MIS REPORT (advisor) → /mis-report ═══════════════════════ */
  {
    parent: "/mis-report",
    children: [
      "/admin/mf-holding-scheme-wise",
      "/admin/share-holding-company-wise",
      "/admin/mf-holding-client-wise",
      "/admin/share-holding-client-wise",
      "/admin/admin-holding-report-client-wise",
      "/admin/systematic-plans-all-clients",
      "/admin/mf-transaction-all-clients",
      "/admin/share-transaction-all-clients",
      "/admin/maturity-investments",
      "/admin/fee-mandates",
      "/admin/insurance-renewals",
      "/admin/mutual-fund-rta-codes",
      "/admin/share-isin",
      "/admin/risk-profile",
      "/admin/systematic-investment",
      "/admin/investment-Account",
      "/admin/returns",
      "/admin/expiring-systematic-plans",
      "/admin/aum-report",
      "/admin/long-term-redemption",
      "/admin/nav-history",
      "/admin/bulk-import-ids",
      "/admin/mis-report/agreement",
      "/admin/80C-report",
      "/admin/brokerage",
      "/admin/send-bulk-reports-by-email",
      "/admin/portfolio-valuation-report",
      "/admin/action-plan-log",
      "/admin/archived-subscribed-services",
      "/cashflow/with-return-report",
      "/reports/schedule-manage",
      "/mis-report/client-investor-report",
      "/mis-report/regular-vs-direct",
      "/mis-report/Advisor-aua",
      "/mis-report/agreement-report",
      "/mis-report/signzy-report",
      "/mis-report/shares",
      "/mis-report/mutual-funds",
    ],
  },
  {
    parent: "/admin/mutual-fund-rta-codes",
    children: ["/admin/archived-mutual-fund-rta-codes"],
  },
  {
    parent: "/admin/advice-log-book",
    children: [
      "/admin/advice-log-book-manual",
      "/admin/advice-log-book-add",
      "/admin/advice-log-book/Archived",
      "/admin/advice-transaction",
    ],
  },
  // MFU Mandates upload is launched from the MFU Mandates import list
  { parent: "/bulk-import-mfu-mandates", children: ["/bulk-import-file-upload/MFU Mandates"] },
  // bulk import is launched from the manual advice log-book
  { parent: "/admin/advice-log-book-manual", children: ["/admin/advice-log-book-import"] },
  // transaction files upload is launched from MF Transactions
  { parent: "/operations/mf/transaction/transactions", children: ["/operations/files_upload"] },
  {
    parent: "/admin/mis-report/agreement",
    children: [
      "/admin/mis-report/add-agreement",
      "/admin/mis-report/create-agreement",
      "/admin/mis-report/edit-agreement",
      "/admin/mis-report/renew-agreement",
      "/admin/mis-report/archived-agreement",
      "/admin/mis-report/upload-agreement",
    ],
  },
  {
    parent: "/admin/brokerage",
    children: [
      "/admin/brokerage/add-brokerage",
      "/admin/brokerage/reconciliation",
      "/admin/brokerage/brokerage-history",
      "/admin/brokerage-manage",
      "/admin/brokerage-summary",
      "/admin/monthly-brokerage-summary",
    ],
  },
  {
    parent: "/reports/schedule-manage",
    children: [
      "/reports/schedule-add",
      "/reports/schedule-edit",
      "/reports/schedule-archived",
    ],
  },

  /* ═══════════════════════ ACCOUNTING (no section page) ═══════════════════════ */
  {
    parent: "/accouting/expense-voucher",
    children: [
      "/accouting/add-expense-vouchers",
      "/accouting/edit-expense-vouchers",
      "/accouting/export-to-excel",
      "/accouting/upload-invoice-bill",
    ],
  },
  {
    parent: "/vendorsupplier/vendor-supplier",
    children: [
      "/vendorsupplier/add-vendor-supplier",
      "/vendorsupplier/edit-vendor-supplier",
      "/vendorsupplier/archived-vendor-supplier",
    ],
  },
  {
    parent: "/accounting/description-master",
    children: [
      "/accounting/add-description-master",
      "/accounting/edit-description-master",
      "/accounting/archived-description-master",
    ],
  },
  {
    parent: "/accounting/client-address",
    children: ["/accounting/edit-client-address"],
  },
  {
    parent: "/accounting/invoice",
    children: [
      "/accounting/view-invoice",
      "/accounting/export-income-to-excel",
      "/accounting/add-invoice",
      "/accounting/edit-invoice",
      "/accounting/fixed-recurring",
      "/accounting/archived-invoice",
      "/accounting/submitted",
      "/accounting/paid",
      "/accounting/future",
    ],
  },
  {
    parent: "/accounting/fixed-recurring",
    children: [
      "/accounting/add-fixed-recurring-invoice",
      "/accounting/archived-fixed-recurring-invoice",
    ],
  },
  {
    parent: "/accounting/company-bank-account",
    children: [
      "/accounting/add-company-bank-account",
      "/accounting/edit-company-bank-account",
      "/accounting/company-bank-account-passbook",
      "/accounting/add-company-bank-account-passbook",
      "/accounting/edit-company-bank-account-passbook",
      "/accounting/view-company-bank-account-passbook",
      "/accounting/import-bank-passbook",
      "/accounting/archived-company-bank-account",
    ],
  },
  {
    parent: "/accounting/company-bank-account-passbook/:id",
    children: [
      "/accounting/add-company-bank-account-passbook/:id",
      "/accounting/edit-company-bank-account-passbook/:id/:id2",
      "/accounting/view-company-bank-account-passbook/:id/:id2",
    ],
  },
  {
    parent: "/accouting/fee-mandates",
    children: [
      "/accouting/add-fee-mandates",
      "/accouting/edit-fee-mandates",
      "/accouting/archived-fee-mandates",
    ],
  },
  {
    parent: "/accounting/payment-details",
    children: [
      "/add-payment-details",
      "/edit-payment-details",
      "/accounting/future-payment-details",
      "/accounting/archived-payment-details",
    ],
  },
  {
    parent: "/slabs",
    children: ["/archived-slabs", "/add-slabs", "/edit-slab"],
  },

  /* ═══════════════════════ MASTERS (advisor; no section page) ═══════════════════════ */
  {
    parent: "/clients",
    children: [
      "/add-clients",
      "/edit-clients",
      "/archived-clients",
      "/admin/draft-clients",
      "/add-new-services",
      "/edit-new-services",
      "/advisor-goals",
      "/category-master",
    ],
  },
  {
    parent: "/category-master",
    children: [
      "/master/category-master-add",
      "/master/category-master-edit",
      "/master/category-master-archived",
    ],
  },
  {
    parent: "/master/announcements",
    children: [
      "/master/add-announcements",
      "/master/edit-announcements",
      "/master/archived-announcements",
    ],
  },
  {
    parent: "/master/planning-assumption",
    children: ["/master/planning-edit-assumption"],
  },
  {
    parent: "/master/planning-model-portfolio",
    children: [
      "/master/planning-add-model-portfolio",
      "/master/planning-edit-model-portfolio",
      "/master/planning-view-edit-model-portfolio",
      "/master/planning-view-model-portfolio",
      "/master/archived-global-parameter", // (ArchivedModelPortfolio — route name is historical)
    ],
  },
  {
    parent: "/master/planning-comments",
    children: [
      "/master/planning-add-comments",
      "/master/planning-edit-comments",
      "/master/planning-archived-comments",
    ],
  },
  {
    parent: "/master/planning-disclaimer-discloser",
    children: ["/master/planning-edit-disclaimer-discloser"],
  },
  {
    parent: "/master/planning-mailing-group",
    children: [
      "/master/planning-add-mailing-group",
      "/master/planning-edit-mailing-group",
      "/master/planning-send-mail-to-group",
      "/master/planning-add-group-user-of",
      "/master/planning-group-user-of",
      "/master/archived-planning-mailing-group",
    ],
  },
  {
    parent: "/master/global-parameter",
    children: [
      "/master/edit-global-parameter",
      "/master/edit-global-parameter-fee-structure",
    ],
  },
  {
    parent: "/master/master-company-master",
    children: [
      "/master/edit-company-master",
      "/master/company-bank-account",
      "/master/planning-sign-off-details",
      "/master/planning-edit-sign-off-details",
    ],
  },
  // accreditation / branch pages return to their own tab on Company Master (read via ?tab=)
  {
    parent: "/master/master-company-master?tab=accreditations",
    children: [
      "/master/master-business-accreditation",
      "/master/master-archived-business-accreditation",
      "/master/masters-add-business-accreditation",
      "/master/masters-edit-business-accreditation",
    ],
  },
  {
    parent: "/master/master-company-master?tab=branches",
    children: [
      "/master/branches",
      "/master/add-branches",
      "/master/edit-branches",
      "/master/archived-branches",
    ],
  },
  {
    parent: "/master/mail-template",
    children: [
      "/master/create-new-template",
      "/master/edit-new-template",
      "/master/view-new-template",
      "/master/create-signature-template",
      "/master/archived-mail-template",
      "/master/copy-record-from-master-template",
    ],
  },
  {
    parent: "/master/recommendation-investment-product",
    children: [
      "/master/add-recommendation-investment-product",
      "/master/edit-recommendation-investment-product",
      "/master/view-edit-recommendation-investment-product",
      "/master/archived-recommendation-investment-product",
    ],
  },
  {
    parent: "/master/desired-asset-allocation",
    children: [
      "/master/add-desired-asset-allocation",
      "/master/archeived-desired-asset-allocation",
    ],
  },
  {
    parent: "/master/Client-Menu-Selection",
    children: [
      "/master/add-Client-Menu-Selection",
      "/master/edit-Client-Menu-Selection",
      "/master/Client-Menu-Packages",
    ],
  },
  {
    parent: "/master/Client-Menu-Packages",
    children: [
      "/master/add-Client-Menu-Package",
      "/master/edit-Client-Menu-Package",
    ],
  },
  {
    parent: "/client-selection-by-menu",
    children: [
      "/master/add-Client-selection-by-Menu",
      "/master/edit-Client-selection-by-Menu",
    ],
  },
  {
    parent: "/master/cis-master",
    children: [
      "/master/add-cis-master",
      "/master/edit-cis-master",
      "/master/archived-cis-master",
    ],
  },
  /* Website compliance masters */
  {
    parent: "/master/website-compliance/Complaints",
    children: [
      "/master/website-compliance/archived-Complaints",
      "/master/website-compliance/histroic-trend",
      "/Complaints-count",
      "/add-complaint",
      "/edit-complaint",
    ],
  },
  {
    parent: "/master/website-compliance/annual-compliance-audit-reports",
    children: ["/add-annual-compliance", "/edit-annual-compliance"],
  },
  /* Risk-profile masters */
  {
    parent: "/master/risk-profile-disclaimer",
    children: [
      "/master/risk-profile-disclaimer-add",
      "/master/risk-profile-disclaimer-edit",
      "/master/risk-profile-disclaimer-archived",
    ],
  },
  {
    parent: "/master/risk-profile-types",
    children: [
      "/master/risk-profile-types/:id",
      "/master/add-risk-profile-type",
      "/master/risk-profile-edit",
      "/master/archived-risk-profile-type",
    ],
  },
  {
    parent: "/master/risk-profile-Q&A",
    children: [
      "/master/add-risk-profile-Q&A",
      "/master/edit-risk-profile-Q&A",
      "/master/archived-risk-profile-Q&A",
    ],
  },

  /* ═══════════════════════ HR (no section page) ═══════════════════════ */
  {
    parent: "/admin/employees",
    children: [
      "/admin/add-employees",
      "/admin/edit-employees",
      "/admin/onboard-employees",
      "/admin/employee-menu",
      "/admin/archived-employees",
      "/admin/draft-employees",
      "/admin/category-master-employees",
      "/admin/archived-category-master-employees",
    ],
  },
  {
    parent: "/Employee-menu-selection",
    children: ["/add-employee-menu-selection", "/edit-employee-menu-selection"],
  },
  {
    parent: "/employee-selection-by-menu",
    children: ["/master/add-employee-selection-by-Menu"],
  },
  {
    parent: "/admin/employee-entry",
    children: ["/admin/edit-employee-entry", "/archivedEmployeeEntry"],
  },
  {
    parent: "/admin/employee-exit",
    children: ["/admin/edit-employee-exit", "/archivedEmployeeExit"],
  },
  {
    parent: "/admin/leave-type",
    children: ["/admin/add-leave-type", "/admin/edit-leave-type"],
  },
  {
    parent: "/admin/holiday-calender",
    children: ["/add-holiday", "/edit-holiday", "/admin/holiday-calender-emplyoee"],
  },

  /* ═══════════════════════ BD ═══════════════════════ */
  {
    parent: "/admin/aua-selection",
    children: ["/admin/edit-aua-selection"],
  },
  {
    parent: "/admin/business-analytics",
    children: [],
  },

  /* ═══════════════════════ RESEARCH ═══════════════════════ */
  {
    parent: "/research/top-performers",
    children: [],
  },
  {
    parent: "/research/scheme-comparison",
    children: [],
  },
  {
    parent: "/research/analyst-estimates",
    children: [
      "/research/analyst-estimates/upload",
      "/research/analyst-estimates/help",
    ],
  },
  {
    parent: "/research/growth-outlook",
    children: [
      "/research/growth-outlook/help",
    ],
  },
  {
    parent: "/research/singleclick-rating",
    children: [
      "/research/singleclick-rating/peers",
      "/research/singleclick-rating/help",
    ],
  },
  {
    parent: "/research/schemes-closed-subscription",
    children: [
      "/research/schemes-closed-subscription/help",
    ],
  },
  {
    parent: "/research/open-nfos",
    children: [
      "/research/open-nfos/help",
    ],
  },
  {
    parent: "/research/scheme-flag-radar",
    children: [
      "/research/scheme-flag-radar/help",
    ],
  },
  {
    parent: "/research/scheme-change-report",
    children: [
      "/research/scheme-change-report/help",
    ],
  },
  {
    parent: "/research/fund-manager-activity",
    children: [
      "/research/fund-manager-activity/help",
    ],
  },

  /* ═══════════════════════ OPERATIONS (advisor) → /operation-header ═══════════════════════ */
  {
    parent: "/operation-header",
    children: [
      "/operations/mf/transaction",
      "/operations/mf/systematic/transaction",
      "/admin/operation/unit-reconciliation",
      "/admin/operation/unit-reconciliation-unmatched",
      "/admin/operation/unit-reconciliation-in-system",
      "/admin/operation/unit-reconciliation-not-rta",
      "/operations/account-opening-mfplatform",
      "/operations/account-opening-mandates",
      "/operations/share/transaction",
      "/operations/share/systematic/transaction",
      "/operations/fixed/income/transaction",
      "/operations/client/information",
      "/operations/client/entry",
      "/operations/client/exit",
      "/bulk-import",
      "/operations/dmat-ledger",
      "/operations/contract-notes",
      "/operations/risk-profile-link",
      "/rta-client-master",
      "/rta-client-master-error",
    ],
  },
  {
    parent: "/operations/mf/transaction",
    children: [
      "/operations/mf/transaction/future",
      "/operations/mf/transaction/platform",
      "/operations/mf/transaction/transactions",
      "/operations/mf/transaction/error",
      "/operations/mf/transaction/ready",
      "/operations/mf/transaction/archive",
      "/operations/mf/transaction/duplicate",
      "/operations/mf/transaction/rejection",
      "/operations/mf/transaction/archived-mf-transactions",
      "/operation/mf/import-transaction",
    ],
  },
  {
    parent: "/operations/mf/systematic/transaction",
    children: [
      "/operations/mf/systematic/future",
      "/operations/mf/systematic/platform",
      "/operations/mf/systematic/transactiontab",
      "/operations/mf/systematic/error",
      "/operations/mf/systematic/ready",
      "/operations/mf/systematic/paused",
      "/operations/mf/systematic/archived",
      "/operations/mf/systematic/import",
      "/operations/mf-systematic/archived",
      "/operation/mf-systematic/import-transaction",
    ],
  },
  {
    parent: "/admin/operation/unit-reconciliation-unmatched",
    children: ["/admin/operation/import-unit-reconciliation"],
  },
  {
    parent: "/operations/share/transaction",
    children: [
      "/operations/share/transaction/future",
      "/operations/share/transaction/inProcess",
      "/operations/share/transaction/platform",
      "/operations/share/transaction/transactions",
      "/operations/share/transaction/error",
      "/operations/share/transaction/ready",
      "/operations/share/transaction/platform/import",
      "/operations/share/archived-transaction",
    ],
  },
  {
    // Archived SIPs is opened from the Share Systematic Transactions page, so its
    // breadcrumb Back returns there (not to the regular share transaction landing).
    parent: "/operations/share/systematic/transaction",
    children: ["/operations/share/transaction/archived-sips"],
  },
  {
    parent: "/operations/files_upload",
    children: ["/operations/files_upload_history"],
  },
  {
    parent: "/operations/client/information",
    children: [
      "/operations/archeived-client/information",
      "/operations/edit-client/information",
    ],
  },
  {
    parent: "/operations/client/entry",
    children: ["/archivedClientsEntry", "/admin/edit-client-entry"],
  },
  {
    parent: "/operations/client/exit",
    children: ["/archivedClientClearance", "/admin/edit-client-exit"],
  },
  {
    parent: "/operations/account-opening-mfplatform",
    children: [
      "/operations/archived-mfu-account",
      "/operations/completed-mfu-account",
      "/operations/add-client-account",
    ],
  },
  {
    parent: "/operations/account-opening-mandates",
    children: [
      "/mandates/add-account-mandate",
      "/mandates/edit-account-mandate",
      "/mandates/archived-account-mandate",
    ],
  },
  {
    parent: "/operations/dmat-ledger",
    children: [
      "/operations/dmat-ledger-others",
      "/operations/dmat-ledger/passbook-entry-manage",
      "/operations/dmat-ledger/passbook-entry-add",
      "/operations/dmat-ledger/passbook-entry-edit",
    ],
  },
  {
    parent: "/operations/dmat-ledger/passbook-entry-manage/:id1",
    children: [
      "/operations/dmat-ledger/passbook-entry-add/:id1",
      "/operations/dmat-ledger/passbook-entry-edit/:id1/:id2",
    ],
  },
  {
    parent: "/rta-client-master",
    children: ["/rta-client-master-upload", "/rta-client-master-archived"],
  },
  /* Bulk Import (Operations › Bulk Import landing) */
  {
    parent: "/bulk-import",
    children: [
      "/bulk-import-master",
      "/bulk-import-clients",
      "/bulk-import-clients-processed",
      "/bulk-import-clients-error",
      "/bulk-import-investors",
      "/bulk-import-investors-error",
      "/bulk-import-processed",
      "/bulk-import-bank-accounts",
      "/bulk-import-bank-error",
      "/bulk-import-mfu-can",
      "/bulk-import-mfu-mandates",
      "/bulk-import-changerm",
      "/bulk-import-file-upload",
      "/bulk-client-mapping",
      "/bulk-client-partially-mapping",
      "/bulk-client-fully-mapping",
    ],
  },
  // RM / SB mapping screens are opened from Bulk Client Mapping, so Back returns there
  {
    parent: "/bulk-client-mapping",
    children: [
      "/bulk-client-rm_1-mapping",
      "/bulk-client-rm_2-mapping",
      "/bulk-client-sb_1-mapping",
      "/bulk-client-sb_2-mapping",
      "/bulk-sb-category-mapping",
    ],
  },
  { parent: "/bulk-import-master", children: ["/bulk-import-master-upload"] },
  {
    parent: "/bulk-import-clients",
    children: ["/bulk-import-clients-menu", "/bulk-import-edit-client"],
  },
  { parent: "/bulk-import-investors", children: ["/bulk-import-edit-investor"] },
  { parent: "/bulk-import-bank-accounts", children: ["/bulk-import-edit-bank"] },
  { parent: "/bulk-import-mfu-can", children: ["/edit-mfu-buffer"] },
  { parent: "/bulk-import-mfu-mandates", children: ["/edit-mfu-mandate-buffer"] },

  /* ═══════════════════════ OPERATIONS (super admin; no section page) ═══════════════════════ */
  {
    parent: "/operations/mftransactionsErrors/mftransactions-mising-schemes",
    children: [
      "/operations/mftransactionsErrors/mftransactions-errors",
      "/operations/mftransactionsErrors/scheme-verify",
      "/operations/mftransactionsErrors/mftransactions-scheme-mapping",
    ],
  },
  {
    parent: "/operations/shareTransaction/sharetransaction-error",
    children: ["/operations/shareTransaction/sharetransaction-missing-code"],
  },
  {
    parent: "/operations/SchemeVerification/matched",
    children: [
      "/operations/SchemeVerification/missingInAccord",
      "/operations/SchemeVerification/missingInAccordRTA",
      "/operations/SchemeVerification/missingInAccordAMFI",
      "/operations/SchemeVerification/missingInSfa",
      "/operations/SchemeVerification/isVerified",
    ],
  },
  {
    parent: "/operations/ShareVerification/matched",
    children: [
      "/operations/ShareVerification/missingInAccord",
      "/operations/ShareVerification/missingInSfa",
      "/operations/ShareVerification/marketCap",
      "/operations/ShareVerification/market-cap-import",
      "/operations/ShareVerification/isSecDetailsFilled",
      "/operations/ShareVerification/missingShare",
      "/operations/ShareVerification/missing",
      "/operations/ShareVerification/missing-nse",
      "/operations/ShareVerification/missing-isin",
      "/operations/ShareVerification/isVerified",
    ],
  },
  {
    parent: "/operations/clientMigration",
    children: ["/operations/add-clientMigration"],
  },
  {
    parent: "/operations/bulkMfTransactions",
    children: ["/operations/files-upload-bulk-mf-transactions"],
  },
  {
    parent: "/operations/bulkMfSystematicTransactions",
    children: ["/operations/files-upload-bulk-mf-systematic-transactions"],
  },
  {
    parent: "/operations/bulk/scheme-verification",
    children: [
      "/operations/bulk/not-verified-scheme",
      "/operations/bulk/missing-scheme",
      "/operations/bulk/category-verified-scheme",
    ],
  },

  /* ═══════════════════════ SA-MASTERS (no section page) ═══════════════════════ */
  {
    parent: "/company-master",
    children: ["/edit-company-master", "/archived-company-master"],
  },
  {
    parent: "/master/adviosr",
    children: ["/master/add-adviosr", "/master/edit-adviosr", "/master/archived-adviosr"],
  },
  {
    parent: "/assumption-master",
    children: [
      "/add-assumption-master",
      "/edit-assumption-master",
      "/archived-assumption-master",
    ],
  },
  {
    parent: "/master/menu-organizer",
    children: [
      "/master/add-menu-organizer",
      "/master/edit-menu-organizer",
      "/master/archived-menu-organizer",
    ],
  },
  {
    parent: "/master/Company-Menu-Selection",
    children: ["/edit/company-Menu-Selection"],
  },
  {
    parent: "/role-management",
    children: [
      "/role-management-permission",
      "/create-role-management",
      "/edit-role-management",
    ],
  },
  {
    parent: "/sa-upload-documents-master",
    children: [
      "/add-upload-documents-master",
      "/actions/edit-document-master",
      "/archived-documents-master",
    ],
  },
  {
    parent: "/goalliablities/goal-liablities",
    children: [
      "/goalliablities/add-goal-liablities",
      "/goalliablities/edit-goal-liablities",
      "/goalliablities/archived-goal-liablities",
    ],
  },
  {
    parent: "/investmentobjective/investment-objective",
    children: [
      "/investmentobjective/add-investment-objective",
      "/investmentobjective/edit-investment-objective",
    ],
  },
  {
    parent: "/sa-client-help-master",
    children: [
      "/information/add-client-help",
      "/information/edit-client-help",
      "/information/archived-client-help",
    ],
  },
  {
    parent: "/sa-admin-help-master",
    children: [
      "/information/add-admin-help",
      "/information/edit-admin-help",
      "/information/archived-admin-help",
    ],
  },
  {
    parent: "/accouting-master/financial-year",
    children: [
      "/accouting-master/add-financial-year",
      "/accouting-master/edit-financial-year",
    ],
  },
  {
    parent: "/accouting-master/cost-Inflation",
    children: [
      "/accouting-master/add-cost-Inflation",
      "/accouting-master/edit-cost-Inflation",
      "/accouting-master/archived-cost-Inflation",
    ],
  },
  {
    parent: "/accouting-master/gst-master",
    children: [
      "/accouting-master/add-gst-master",
      "/accouting-master/edit-gst-master",
      "/accouting-master/archived-gst-master",
    ],
  },
  {
    parent: "/master/client-onboarding-checklist-master",
    children: [
      "/master/add-client-onboarding-checklist-master",
      "/master/edit-client-onboarding-checklist-master",
      "/master/archived-client-onboarding-checklist-master",
    ],
  },
  {
    parent: "/master/client-clearance-checklist-master",
    children: [
      "/master/add-client-clearance-checklist-master",
      "/master/edit-client-clearance-checklist-master",
      "/master/archived-client-clearance-checklist-master",
    ],
  },
  {
    parent: "/master/employee-onboarding-checklist-master",
    children: [
      "/master/add-employee-onboarding-checklist-master",
      "/master/edit-employee-onboarding-checklist-master",
      "/master/archived-employee-onboarding-checklist-master",
    ],
  },
  {
    parent: "/master/employee-clearance-checklist-master",
    children: [
      "/master/add-employee-clearance-checklist-master",
      "/master/edit-employee-clearance-checklist-master",
      "/master/archived-employee-clearance-checklist-master",
    ],
  },
  {
    parent: "/master/notification-master",
    children: [
      "/master/add-notification-master",
      "/master/edit-notification-master",
      "/master/archived-notification-master",
    ],
  },
  {
    parent: "/master/notification-title",
    children: [
      "/master/add-notification-title",
      "/master/edit-notification-title",
      "/master/archived-notification-title",
    ],
  },
  {
    parent: "/master/notification-status",
    children: [
      "/master/archived-notification-status",
      "/master/read-notification-status",
    ],
  },
  {
    parent: "/master/task-master",
    children: ["/add-task-master", "/edit-task-master", "/archived-task-master"],
  },
  {
    parent: "/mail-template",
    children: ["/add-mail-template", "/edit-mail-template"],
  },

  /* ═══════════════════════ PRODUCT MASTERS (super admin; no section page) ═══════════════════════ */
  {
    parent: "/information/product-categories",
    children: [
      "/information/add-product-categories",
      "/information/edit-product-categories",
      "/information/archived-product-categories",
    ],
  },
  {
    parent: "/product-master/client-portfolio",
    children: [
      "/product-master/add-client-portfolio",
      "/product-master/edit-client-portfolio",
      "/product-master/archived-client-portfolio",
    ],
  },
  /* Insurance masters */
  {
    parent: "/insurance/types",
    children: ["/insurance/add-types", "/insurance/edit-types", "/insurance/archived-types"],
  },
  {
    parent: "/insurance/subTypes",
    children: [
      "/insurance/add-subTypes",
      "/insurance/edit-subTypes",
      "/insurance/archived-subTypes",
    ],
  },
  {
    parent: "/insurance/benifits-available",
    children: [
      "/insurance/add-benifits-available",
      "/insurance/edit-benifits-available",
      "/insurance/archived-benifits-available",
    ],
  },
  {
    parent: "/insurance/natureof-policy",
    children: [
      "/insurance/add-natureof-policy",
      "/insurance/edit-natureof-policy",
      "/insurance/archived-natureof-policy",
    ],
  },
  {
    parent: "/insurance/riders",
    children: [
      "/insurance/add-insurance-riders",
      "/insurance/edit-insurance-riders",
      "/insurance/archived-insurance-riders",
    ],
  },
  {
    parent: "/insurance/categories",
    children: [
      "/insurance/add-categories",
      "/insurance/edit-categories",
      "/insurance/archived-categories",
    ],
  },
  {
    parent: "/insurance/annuity-types",
    children: [
      "/insurance/add-annuity-types",
      "/insurance/edit-annuity-types",
      "/insurance/archived-annuity-types",
    ],
  },
  {
    parent: "/insurance/transactions-type",
    children: [
      "/insurance/add-transactions-type",
      "/insurance/edit-transactions-type",
      "/insurance/archived-transactions-type",
    ],
  },
  {
    parent: "/operationTransactionTypeMaster/transactions-type",
    children: [
      "/operation-Transaction-Type-Master/add-transactions-type",
      "/operation-Transaction-Type-Master/edit-transactions-type",
      "/operation-Transaction-Type-Master/archived-transactions-type",
    ],
  },
  /* Mutual-fund masters */
  {
    parent: "/mutualfunds/taxaction",
    children: [
      "/mutualfunds/add-taxaction",
      "/mutualfunds/edit-taxaction",
      "/mutualfunds/archived-taxaction",
    ],
  },
  {
    parent: "/mutualfunds/categories",
    children: [
      "/mutualfunds/add-categories",
      "/mutualfunds/edit-categories",
      "/mutualfunds/archived-categories",
    ],
  },
  {
    parent: "/mutualfunds/sub-categories",
    children: [
      "/mutualfunds/add-sub-categories",
      "/mutualfunds/edit-sub-categories",
      "/mutualfunds/archived-sub-categories",
    ],
  },
  /* Share masters */
  {
    parent: "/share-master/market-cap",
    children: [
      "/share-master/add-market-cap",
      "/share-master/edit-market-cap",
      "/share-master/market-cap-archived",
    ],
  },
  {
    parent: "/share-master/share-product-type",
    children: [
      "/share-master/add-product-type",
      "/share-master/edit-product-type",
      "/share-master/product-type-archived",
    ],
  },
  {
    parent: "/share-master/asset-class",
    children: [
      "/share-master/add-asset-class",
      "/share-master/edit-asset-class",
      "/share-master/asset-class-archived",
    ],
  },
  {
    parent: "/share-master/industry",
    children: [
      "/share-master/add-industry",
      "/share-master/edit-industry",
      "/share-master/industry-archived",
    ],
  },
  {
    parent: "/share-master/sector",
    children: [
      "/share-master/add-sector",
      "/share-master/edit-sector",
      "/share-master/sector-archived",
    ],
  },
  {
    parent: "/share-master/main-sector",
    children: [
      "/share-master/add-main-sector",
      "/share-master/edit-main-sector",
      "/share-master/main-sector-archived",
    ],
  },
  {
    parent: "/share-master/sub-sector",
    children: [
      "/share-master/add-sub-sector",
      "/share-master/edit-sub-sector",
      "/share-master/sub-sector-archived",
    ],
  },
  {
    parent: "/share-master/micro-sector",
    children: [
      "/share-master/add-micro-sector",
      "/share-master/edit-micro-sector",
      "/share-master/micro-sector-archived",
    ],
  },
  {
    parent: "/share-master/product-services",
    children: [
      "/share-master/add-product-services",
      "/share-master/edit-product-services",
      "/share-master/product-services-archived",
    ],
  },
  {
    parent: "/share-master/moat-manage",
    children: ["/share-master/add-moat", "/share-master/edit-moat", "/share-master/moat-archived"],
  },
  {
    parent: "/share-master/share-model",
    children: ["/share-master/add-model", "/share-master/edit-model", "/share-master/model-archived"],
  },
  {
    parent: "/share-master/cycle",
    children: ["/share-master/add-cycle", "/share-master/edit-cycle", "/share-master/cycle-archived"],
  },
  {
    parent: "/share-master/share-profile",
    children: [
      "/share-master/add-profile",
      "/share-master/edit-profile",
      "/share-master/profile-archived",
    ],
  },
  /* Income masters */
  {
    parent: "/income/income-categories",
    children: [
      "/income/add-income-categories",
      "/income/edit-income-categories",
      "/income/archived-income-categories",
    ],
  },
  {
    parent: "/income/income-heads",
    children: [
      "/income/add-income-heads",
      "/income/edit-income-heads",
      "/income/archived-income-heads",
    ],
  },
  {
    parent: "/product-master/ulip-funds",
    children: [
      "/product-master/add-ulip-funds",
      "/product-master/edit-ulip-funds",
      "/product-master/archived-ulip-funds",
    ],
  },
  {
    parent: "/bonds-debenture",
    children: ["/add-bonds-debenture", "/edit-bonds-debenture", "/archived-bonds-debenture"],
  },
  /* Real-estate masters */
  {
    parent: "/realstate/ownership-status",
    children: [
      "/realestate/add-ownership-status",
      "/realestate/edit-ownership-status",
      "/realstate/archived-ownership-status",
    ],
  },
  {
    parent: "/realstate/property-type",
    children: [
      "/realstate/add-property-type",
      "/realstate/edit-property-type",
      "/realstate/archived-property-type",
    ],
  },
  /* Household masters */
  {
    parent: "/household/household-categories",
    children: [
      "/household/add-household-categories",
      "/household/edit-household-categories",
      "/household/archived-household-categories",
    ],
  },
  {
    parent: "/household/household-assets",
    children: [
      "/household/add-household-assets",
      "/household/edit-household-assets",
      "/household/archived-household-assets",
    ],
  },
  {
    parent: "/household/household-vehicles",
    children: ["/household/add-household-vehicles", "/household/edit-household-vehicles"],
  },
  {
    parent: "/bank/loan-type",
    children: ["/bank/add-loan-type", "/bank/edit-loan-type", "/bank/archived-loan-type"],
  },
  {
    parent: "/product-master/global-product-parameter",
    children: [
      "/product-master/add-global-product-parameter",
      "/product-master/edit-global-product-parameter",
      "/product-master/archived-global-product-parameter",
    ],
  },
  /* Establishment masters */
  {
    parent: "/establishment/establishment-types",
    children: [
      "/establishment/add-establishment-types",
      "/establishment/edit-establishment-types",
      "/establishment/archived-establishment-types",
    ],
  },
  {
    parent: "/establishment/insurance-companies",
    children: [
      "/establishment/add-insurance-companies",
      "/establishment/edit-insurance-companies",
      "/establishment/archived-insurance-companies",
    ],
  },
  {
    parent: "/establishment/establishment-bank",
    children: [
      "/establishment/add-establishment-bank",
      "/establishment/edit-establishment-bank",
      "/establishment/archived-establishment-bank",
    ],
  },
  {
    parent: "/establishment/establishment-branch",
    children: [
      "/establishment/add-establishment-branch",
      "/establishment/edit-establishment-branch",
      "/establishment/archived-establishment-branch",
    ],
  },
  {
    parent: "/establishment/establishment-financial-institution",
    children: [
      "/establishment/add-establishment-financial-institution",
      "/establishment/edit-establishment-financial-institution",
      "/establishment/archived-establishment-financial-institution",
    ],
  },
  {
    parent: "/establishment/establishment-depository-participants",
    children: [
      "/establishment/add-establishment-depository-participants",
      "/establishment/edit-establishment-depository-participants",
      "/establishment/archived-establishment-depository-participants",
    ],
  },
  {
    parent: "/establishment/mutual-fund",
    children: [
      "/establishment/add-mutual-fund",
      "/establishment/edit-mutual-fund",
      "/establishment/archived-mutual-fund",
    ],
  },
  {
    parent: "/establishment/p2p-lending",
    children: [
      "/establishment/add-p2p-lending",
      "/establishment/edit-p2p-lending",
      "/establishment/archived-p2p-lending",
    ],
  },
  {
    parent: "/establishment/pms-providers-manage",
    children: [
      "/establishment/pms-providers-add",
      "/establishment/pms-providers-edit",
      "/establishment/pms-providers-archived",
    ],
  },
  {
    parent: "/establishment/Aif-providers-manage",
    children: [
      "/establishment/Aif-providers-add",
      "/establishment/Aif-providers-edit",
      "/establishment/Aif-providers-archived",
    ],
  },
  /* Product parameters */
  {
    parent: "/product-parameter/insurance-product",
    children: [
      "/product-parameter/add-insurance-product",
      "/product-parameter/edit-insurance-product",
      "/product-parameter/archived-insurance-product",
    ],
  },
  {
    parent: "/product-parameter/mutual-funds",
    children: [
      "/product-parameter/add-mutual-funds",
      "/product-parameter/edit-mutual-funds",
      "/product-parameter/archived-mutual-funds",
      "/product-parameter/nav-history-mutual-funds",
      "/product-parameter/add-nav-history-mutual-funds",
      "/product-parameter/quantitative-indicators-mutual-funds",
      "/product-parameter/add-quantitative-indicators-mutual-funds",
    ],
  },
  {
    parent: "/product-parameter/nav-history-mutual-funds/:id",
    children: ["/product-parameter/add-nav-history-mutual-funds/:id"],
  },
  {
    parent: "/product-parameter/quantitative-indicators-mutual-funds/:id",
    children: ["/product-parameter/add-quantitative-indicators-mutual-funds/:id"],
  },
  {
    parent: "/product-parameter/shares",
    children: [
      "/product-parameter/add-shares",
      "/product-parameter/edit-shares",
      "/product-parameter/archived-shares",
      "/product-parameter/shares-history-Price",
      "/product-parameter/add-shares-history-Price",
      "/product-parameter/edit-shares-history-Price",
      "/product-parameter/shares-quaterly-historic",
      "/product-parameter/add-shares-quaterly-historic",
    ],
  },
  {
    parent: "/product-parameter/shares-quaterly-historic/:id",
    children: ["/product-parameter/add-shares-quaterly-historic/:id"],
  },
  {
    parent: "/Product-parameters/Fixed-return-and-retirement-products",
    children: [
      "/Product-parameters/add-fixed-return-and-retirement-products",
      "/Product-parameters/edit-fixed-return-and-retirement-products",
      "/Product-parameters/archived-fixed-return-and-retirement-products",
      "/product-parameter/fixed-return-&-retirement",
    ],
  },
  {
    parent: "/product-masters/pms-providers-manage",
    children: [
      "/product-masters/pms-providers-add",
      "/product-masters/pms-providers-edit",
      "/product-masters/pms-providers-archived",
    ],
  },
  {
    parent: "/product-masters/Aif-providers-manage",
    children: [
      "/product-masters/Aif-providers-add",
      "/product-masters/Aif-providers-edit",
      "/product-masters/Aif-providers-archived",
    ],
  },

  /* ═══════════════════════ ADVISORY → /advisory-header ═══════════════════════ */
  {
    parent: "/advisory-header",
    children: [
      "/financial-plans",
      "/tax-plans",
      "/incidental-advice-report",
      "/financial/plan/advice/request",
      "/financial/plan/proactive/advice-cancellation",
      "/financial/plan/proactive/advice-investment",
      "/financial/plan/proactive/advice-redemption",
      "/financial/plan/proactive/advice-switch",
      "/financial/plan/proactive/advice-stp",
      "/financial/plan/proactive/advice-swp",
      "/financial/plan/proactive/advice-bulk-investment",
      "/financial/plan/proactive/advice-bulk-redemption",
      "/financial/plan/proactive/rebalancing",
      "/financial/plan/investment/purpose",
      "/financial/plan/redemption/purpose",
      "/review",
      "/financial-planing/client-portfolio",
    ],
  },
  {
    // Every step of the Financial-Plan wizard → the Plans list
    parent: "/financial-plans",
    children: [
      "/financial-plans-homePage",
      "/financial-plans-cover-page",
      "/financial-plans-bank-and-dmat",
      "/financial-plans-share",
      "/financial-plans-mutual-funds",
      "/financial-plans-pms",
      "/financial-plans-aif",
      "/financial-plans-ulip",
      "/financial-plans-moneyback",
      "/financial-plans-fixed-income",
      "/financial-plans-retirement-products",
      "/financial-plans-bullion",
      "/financial-plans-realestate",
      "/financial-plans-others",
      "/financial-planing",
    ],
  },
  {
    parent: "/financial-planing/client-portfolio",
    children: ["/financial-planing/client-portfolio-passbook"],
  },
  {
    parent: "/incidental-advice-report",
    children: ["/incidental-advice-report-homePage", "/incidental-advice"],
  },
  {
    parent: "/financial/plan/advice/request",
    children: [
      "/financial/plan/advice/request-sent-for-approval",
      "/financial/plan/advice/request-completed",
      "/financial/plan/advice/request-deleted",
      "/financial/plan/advice/lumpSum",
      "/financial/plan/advice/sip-investment",
      "/financial/plan/advice/sip-cancellation",
      "/financial/plan/advice/sip-pause",
      "/financial/plan/advice/redemption",
    ],
  },
  {
    parent: "/financial/plan/proactive/advice-cancellation",
    children: [
      "/financial/plan/proactive/advice-cancellation-add",
      "/financial/plan/proactive/advice-activesplit",
      "/financial/plan/proactive/advice-completed",
      "/financial/plan/proactive/advice-deleted",
    ],
  },
  {
    parent: "/financial/plan/proactive/advice-investment",
    children: [
      "/financial/plan/proactive/add-investment-request",
      "/financial/plan/proactive/edit-proactive-investment-request",
      "/financial/plan/proactive/advice-investment-send-for-approval",
      "/financial/plan/proactive/advice-investment-completed",
      "/financial/plan/proactive/advice-investment-deleted",
    ],
  },
  {
    parent: "/financial/plan/proactive/rebalancing",
    children: [
      "/financial/plan/proactive/add-rebalancing-request",
      "/financial/plan/proactive/edit-rebalancing-request",
      "/financial/plan/proactive/rebalancing-send-for-approval",
      "/financial/plan/proactive/rebalancing-completed",
      "/financial/plan/proactive/rebalancing-deleted",
    ],
  },
  {
    parent: "/financial/plan/proactive/advice-redemption",
    children: [
      "/financial/plan/proactive/add-proactive-redemption-request",
      "/financial/plan/proactive/edit-proactive-redemption-request",
      "/financial/plan/proactive/advice-redemption-send-for-approval",
      "/financial/plan/proactive/advice-redemption-completed",
      "/financial/plan/proactive/advice-redemption-deleted",
    ],
  },
  {
    parent: "/financial/plan/proactive/advice-switch",
    children: [
      "/financial/plan/proactive/add-switch-request",
      "/financial/plan/proactive/edit-proactive-switch-request",
      "/financial/plan/proactive/advice-switch-send-for-approval",
      "/financial/plan/proactive/advice-switch-completed",
      "/financial/plan/proactive/advice-switch-deleted",
    ],
  },
  {
    parent: "/financial/plan/proactive/advice-stp",
    children: [
      "/financial/plan/proactive/add-stp-request",
      "/financial/plan/proactive/edit-proactive-stp-request",
      "/financial/plan/proactive/advice-stp-send-for-approval",
      "/financial/plan/proactive/advice-stp-completed",
      "/financial/plan/proactive/advice-stp-deleted",
    ],
  },
  {
    parent: "/financial/plan/proactive/advice-swp",
    children: [
      "/financial/plan/proactive/add-swp-request",
      "/financial/plan/proactive/edit-proactive-swp-request",
      "/financial/plan/proactive/advice-swp-send-for-approval",
      "/financial/plan/proactive/advice-swp-completed",
      "/financial/plan/proactive/advice-swp-deleted",
    ],
  },
  {
    parent: "/financial/plan/proactive/advice-bulk-investment",
    children: [
      "/financial/plan/proactive/add-proactive-investment-request",
      "/financial/plan/proactive/edit-proactive-bulk-investment-request",
      "/financial/plan/proactive/advice-bulk-investment-send-for-approval",
      "/financial/plan/proactive/advice-bulk-investment-completed",
      "/financial/plan/proactive/advice-bulk-investment-deleted",
      "/financial/plan/proactive/advice-bulk-investment-approval-deleted",
    ],
  },
  {
    parent: "/financial/plan/proactive/advice-bulk-redemption",
    children: [
      "/financial/plan/proactive/add-proactive-bulk-redemption-request",
      "/financial/plan/proactive/edit-proactive-bulk-redemption-request",
      "/financial/plan/proactive/advice-bulk-redemption-send-for-approval",
      "/financial/plan/proactive/advice-bulk-redemption-completed",
      "/financial/plan/proactive/advice-bulk-redemption-deleted",
      "/financial/plan/proactive/advice-bulk-redemption-approval-deleted",
    ],
  },
  {
    parent: "/financial/plan/investment/purpose",
    children: [
      "/financial/plan/investment/purpose/share",
      "/financial/plan/investment/purpose/historical-data",
      "/financial/plan/investment/purpose/historical-data-share",
      "/financial/plan/investment/purpose-completed",
      "/financial/plan/investment/purpose-share-completed",
    ],
  },
  {
    parent: "/financial/plan/redemption/purpose",
    children: [
      "/financial/plan/redemption/purpose/share",
      "/financial/plan/redemption/purpose/historical-data",
      "/financial/plan/redemption/purpose/historical-data-share",
      "/financial/plan/redemption/purpose-completed",
      "/financial/plan/redemption/purpose-share-completed",
    ],
  },
  {
    parent: "/review",
    children: ["/review-details", "/review-completed", "/review-archived", "/review-calendar"],
  },

  /* ═══════════════════════ COMPLIANCE → /compliance-header ═══════════════════════ */
  {
    parent: "/compliance-header",
    children: [
      "/contra-trade-report",
      "/publish-restricted-list",
      "/restricted-list",
      "/trade-request",
      "/trade-approval",
      "/admin-aua-report-client-wise",
      "/admin-aua-report-complaints",
      "/regulatory-updates",
      "/clients/pan-varification",
      "/admin/advice-log-book",
    ],
  },
  {
    parent: "/publish-restricted-list",
    children: ["/publish-restricted-list/add", "/publish-restricted-list/edit"],
  },
  { parent: "/restricted-list", children: ["/archived-restricted-list"] },
  {
    parent: "/trade-request",
    children: [
      "/trade-request-add",
      "/trade-request-edit",
      "/trade-request-archived",
      "/view-trade-request",
    ],
  },
];

/* ───────────────────────── matching ───────────────────────── */

type Match = { len: number; params: Record<string, string> };

function matchChild(path: string, pattern: string): Match | null {
  // routes match case-insensitively, so the parent lookup must too
  if (pattern.indexOf(":") === -1) {
    const lp = path.toLowerCase();
    const lc = pattern.toLowerCase();
    if (lp === lc || lp.startsWith(lc + "/")) {
      return { len: pattern.length, params: {} };
    }
    return null;
  }
  const ps = path.split("/");
  const cs = pattern.split("/");
  if (ps.length < cs.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < cs.length; i++) {
    if (cs[i].charAt(0) === ":") {
      if (!ps[i]) return null;
      params[cs[i].slice(1)] = ps[i];
    } else if (cs[i].toLowerCase() !== ps[i].toLowerCase()) {
      return null;
    }
  }
  return { len: pattern.length, params };
}

function fillParent(parent: string, params: Record<string, string>): string {
  return parent.replace(/:(\w+)/g, (_m, k) => params[k] ?? "");
}

/** Resolve the menu-parent route for a pathname, or null if not mapped. */
export function getMenuParent(pathname: string): string | null {
  let raw = (pathname || "").split("?")[0].split("#")[0];
  // URLs with spaces arrive encoded (e.g. /bulk-import-file-upload/MFU%20Mandates)
  try { raw = decodeURIComponent(raw); } catch { /* keep as-is */ }
  const path = raw.replace(/\/+$/, "") || "/";
  let best: { parent: string; match: Match } | null = null;
  for (const g of GROUPS) {
    for (const c of g.children) {
      const m = matchChild(path, c);
      if (m && (!best || m.len > best.match.len)) best = { parent: g.parent, match: m };
    }
  }
  if (!best) return null;
  const resolved = fillParent(best.parent, best.match.params);
  // never resolve a page to itself
  return resolved.toLowerCase() === path.toLowerCase() ? null : resolved;
}

export default getMenuParent;
